﻿import {
  socialServiceRunnersListUsers,
  techniciansListUsers,
  unwrap,
  usersChangeOwnPassword,
  usersListUsers,
} from "../../api";
import { PartialListDto, PasswordChangeResultDto, UserDto, UserListDto } from "../../api/types";
import { isValueUsed } from "./UniquenesApi";
import { usersOps } from "../../api/operations";

/**
 * Gets whether user's email is already used
 * @param value
 * @param message
 */
export const isEmailUsed = (value: string, message: string): Promise<void> =>
  isValueUsed<UserDto, string>(usersOps.valueUsed, "email", value, message);

export function changePassword(value: any) {
  return unwrap(usersChangeOwnPassword({ body: value })) as Promise<PasswordChangeResultDto>;
}

/**
 * Verifies that password was not pawned
 * @param passwordHash SHA1 hash of the password
 * @returns Number of times the password was pawned
 */
export async function passwordPawnedCount(passwordHash: string): Promise<number> {
  const start = passwordHash.substring(0, 5);
  const rest = passwordHash.substring(5).toUpperCase();
  try {
    const response = await fetch(`https://api.pwnedpasswords.com/range/${start}`, {
      headers: {
        "Add-Padding": "true",
      },
    });
    const data: string = await response.text();
    const lines = data.split("\n");
    const result = lines.find((r) => r.startsWith(rest))?.split(":");
    if (result && result[1]) {
      return parseInt(result[1], 10);
    }
    return 0;
  } catch (err) {
    // On error, just return 0
    return 0;
  }
}
/** The three list operations this is called with. */
type InfiniteListCall =
  | typeof usersListUsers
  | typeof techniciansListUsers
  | typeof socialServiceRunnersListUsers;

export async function getInfiniteList(
  listUsers: InfiniteListCall,
  {
    skip,
    max,
    fulltext,
    customerId,
  }: {
    skip: number;
    max: number;
    fulltext?: string;
    customerId?: number;
  },
  initialValue?: UserListDto
) {
  // `listUsers` is a union of three functions, so its options type is a union too and
  // TypeScript will not accept one object against it. All three endpoints accept exactly
  // these four parameters — verified against the document — so the cast is safe rather
  // than lazy.
  const response = (await unwrap(
    listUsers({ query: { max, skip, fulltext, customerId } } as never)
  )) as PartialListDto<UserListDto>;
  return {
    ...response,
    rowCount: initialValue ? response.rowCount + 1 : response.rowCount,
    results: [
      { name: initialValue?.username, id: initialValue?.id },
      ...response.results.map((i) => ({ ...i, name: i.username })),
    ].filter((i) => i?.id) as UserListDto[],
  };
}

export async function getUserInfiniteList(
  params: { skip: number; max: number; fulltext?: string; customerId?: number },
  initialValue?: UserListDto
) {
  return getInfiniteList(usersListUsers, params, initialValue);
}

export async function getTechnicianInfiniteList(
  params: { skip: number; max: number; fulltext?: string; customerId?: number },
  initialValue?: UserListDto
) {
  // Was "technician/list" — singular, which is not a route. The list silently 404ed.
  return getInfiniteList(techniciansListUsers, params, initialValue);
}

export async function getSocialServiceRunnersList(
  params: { skip: number; max: number; fulltext?: string; customerId?: number },
  initialValue?: UserListDto
) {
  return getInfiniteList(socialServiceRunnersListUsers, params, initialValue);
}
